import AccountIntervalSpecification from "../../accountSpecification/AccountIntervalSpecification.js";
import CashConversionConfiguration from "../../accountSpecification/CashConversionConfiguration.js";
import Model from "../../Model.js";
import MonthPeriod from "../MonthPeriod.js";
import SingleAccountSpecification from "../../accountSpecification/SingleAccountSpecification.js";
import specificationFactory from "../../specification/specificationFactory.js";
import VatPaymentSettingModel from "../VatPaymentSettingModel.js";

/**
 * Remember user-specified options for a `ForecastTemplate`.
 *
 * @param {integer} [numberOfReferencePeriods] - Number of months of actual
 *   accounting data to take into account.  For example, if
 *   numberOfReferencePeriods=12 and the user opens a forecast template in
 *   March 2022, the accounting data from April 2021 to March 2022 will
 *   be used to guess predicted periods
 * @param {integer} [numberOfPredictedPeriods] - Number of months we want
 *   to guess accounting data for. For example, if
 *   numberOfPredictedPeriods=12 and the user opens a forecast template in
 *   March 2022, the computation engine will guess accounting data from
 *   April 2022 to March 2023
 * @param {string} [chartOfAccountsId] - The unique identifier of the
 *   chart of accounts to use to interpret the forecast template
 * @param {string} [bankAccount] - Bank account number affected by forecast
 *   income and cost.
 * @param {CashConversionConfiguration} forecastAccountsPayableCashConversionConfiguration - Configuration to calculate payable forecasted cash conversions
 * @param {CashConversionConfiguration} forecastAccountsReceivableCashConversionConfiguration - Configuration to calculate receivable forecasted cash conversions
 * @param {AbstractSpecification} existingAccountsPayable - Account Specification
 *   on which the residuals are calculated on.
 * @param {number[]} [existingAccountsPayableDelays] - Divisions of the accounts payable delays.
 *   The array length MUST be 3, and the total sum MUST be 100
 * @param {number[]} [accountsReceivableDelays] - Divisions of the accounts receivable delays.
 *   The array length MUST be 3, and the total sum MUST be 100
 * @param {string} [forecastTemplateDescription] - Description of the overarching
 *   prerequisites of the templates on a high-level view
 * @param {AbstractSpecification} [incomingVatGroup] - Set of accounts
 *   containing VAT collected by the company
 * @param {AbstractSpecification} [outgoingVatGroup] - Set of accounts
 *   containing VAT paid to the suppliers
 * @param {AbstractSpecification} [sumVatGroup] - Intermediate set of
 *   accounts used to compute the difference between collected and repaid VAT
 * @param {Month} [frozenCurrentPeriod] - Current period of the fiscal
 *   year regardless of the last imported periods. If falsy, the
 *   current period will be the last active one.
 */
export default class ForecastConfiguration extends Model {
	constructor({
		numberOfReferencePeriods = 12,
		numberOfPredictedPeriods = 12,
		chartOfAccountsId = "",
		bankAccount = "",
		forecastAccountsPayableCashConversionConfiguration = new CashConversionConfiguration(),
		existingAccountsPayable = new AccountIntervalSpecification(),
		existingAccountsPayableDelays = [0],
		forecastAccountsReceivableCashConversionConfiguration = new CashConversionConfiguration(),
		existingAccountsReceivable = new AccountIntervalSpecification(),
		existingAccountsReceivableDelays = [0],
		taxAccount = undefined,
		incomingVat = undefined,
		outgoingVat = undefined,
		vatSumAccount = undefined,
		incomingVatGroup = undefined,
		outgoingVatGroup = undefined,
		sumVatGroup = undefined,
		vatPaymentModel = new VatPaymentSettingModel(),
		forecastTemplateDescription = "",
		frozenCurrentPeriod = null,
	} = {}) {
		super(...arguments);

		this._numberOfReferencePeriods = numberOfReferencePeriods;
		this._numberOfPredictedPeriods = numberOfPredictedPeriods;
		this._chartOfAccountsId = chartOfAccountsId;
		this._bankAccount = bankAccount;
		this._existingAccountsPayable = existingAccountsPayable;
		this._existingAccountsPayableDelays = existingAccountsPayableDelays;
		this._existingAccountsReceivable = existingAccountsReceivable;

		this._existingAccountsReceivableDelays =
			existingAccountsReceivableDelays;

		this._taxAccount = taxAccount;
		this._incomingVat = incomingVat;
		this._outgoingVat = outgoingVat;
		this._vatSumAccount = vatSumAccount;
		this._incomingVatGroup = incomingVatGroup;
		this._outgoingVatGroup = outgoingVatGroup;
		this._sumVatGroup = sumVatGroup;
		this._vatPaymentModel = vatPaymentModel;
		this._forecastTemplateDescription = forecastTemplateDescription;

		this._forecastAccountsPayableCashConversionConfiguration =
			forecastAccountsPayableCashConversionConfiguration;

		this._forecastAccountsReceivableCashConversionConfiguration =
			forecastAccountsReceivableCashConversionConfiguration;

		this._frozenCurrentPeriod = frozenCurrentPeriod;
	}

	getNumberOfReferencePeriods() {
		return this._numberOfReferencePeriods;
	}

	getFrozenCurrentPeriod() {
		return this._frozenCurrentPeriod;
	}

	setFrozenCurrentPeriod(period) {
		this._frozenCurrentPeriod = period;
	}

	setNumberOfReferencePeriods(numberOfReferencePeriods) {
		this._numberOfReferencePeriods = numberOfReferencePeriods;
	}

	getNumberOfPredictedPeriods() {
		return this._numberOfPredictedPeriods;
	}

	setNumberOfPredictedPeriods(numberOfPredictedPeriods) {
		this._numberOfPredictedPeriods = numberOfPredictedPeriods;
	}

	getChartOfAccountsId() {
		return this._chartOfAccountsId;
	}

	getBankAccount() {
		return this._bankAccount;
	}

	setBankAccount(value) {
		this._bankAccount = value;
	}

	getForecastAccountsPayable() {
		return this._forecastAccountsPayableCashConversionConfiguration.getResidualAccountSpecification();
	}

	setForecastAccountsPayable(value) {
		this._forecastAccountsPayableCashConversionConfiguration.setResidualAccountSpecification(
			new SingleAccountSpecification({
				number: value,
			}),
		);
	}

	getForecastAccountsPayableDelays() {
		return this._forecastAccountsPayableCashConversionConfiguration.getResidualsSpread();
	}

	setForecastAccountsPayableDelays(value) {
		return this._forecastAccountsPayableCashConversionConfiguration.setResidualsSpread(
			value,
		);
	}

	getExistingAccountsPayable() {
		return this._existingAccountsPayable;
	}

	setExistingAccountsPayable(value) {
		this._existingAccountsPayable = value;
	}

	getExistingAccountsPayableDelays() {
		return this._existingAccountsPayableDelays;
	}

	setExistingAccountsPayableDelays(values) {
		this._existingAccountsPayableDelays = values;
	}

	getForecastAccountsReceivable() {
		return this._forecastAccountsReceivableCashConversionConfiguration.getResidualAccountSpecification();
	}

	setForecastAccountsReceivable(value) {
		this._forecastAccountsReceivableCashConversionConfiguration.setResidualAccountSpecification(
			new SingleAccountSpecification({
				number: value,
			}),
		);
	}

	getForecastAccountsReceivableDelays() {
		return this._forecastAccountsReceivableCashConversionConfiguration.getResidualsSpread();
	}

	setForecastAccountsReceivableDelays(value) {
		this._forecastAccountsReceivableCashConversionConfiguration.setResidualsSpread(
			value,
		);
	}

	getExistingAccountsReceivable() {
		return this._existingAccountsReceivable;
	}

	setExistingAccountsReceivable(value) {
		this._existingAccountsReceivable = value;
	}

	getExistingAccountsReceivableDelays() {
		return this._existingAccountsReceivableDelays;
	}

	setExistingAccountsReceivableDelays(values) {
		this._existingAccountsReceivableDelays = values;
	}

	getIncomingVat() {
		return this._incomingVat;
	}

	setIncomingVat(value) {
		this._incomingVat = value;
	}

	getOutgoingVat() {
		return this._outgoingVat;
	}

	setOutgoingVat(value) {
		this._outgoingVat = value;
	}

	getVatSumAccount() {
		return this._vatSumAccount;
	}

	setVatSumAccount(value) {
		this._vatSumAccount = value;
	}

	getTaxAccount() {
		return this._taxAccount;
	}

	setTaxAccount(value) {
		this._taxAccount = value;
	}

	getIncomingVatGroup() {
		return this._incomingVatGroup;
	}

	setIncomingVatGroup(value) {
		this._incomingVatGroup = value;
	}

	getOutgoingVatGroup() {
		return this._outgoingVatGroup;
	}

	setOutgoingVatGroup(value) {
		this._outgoingVatGroup = value;
	}

	getSumVatGroup() {
		return this._sumVatGroup;
	}

	setSumVatGroup(value) {
		this._sumVatGroup = value;
	}

	getVatPaymentModel() {
		return this._vatPaymentModel;
	}

	setVatPaymentModel(value) {
		this._vatPaymentModel = value;
	}

	getVatPaymentPeriod() {
		return this._vatPaymentModel.getVatPaymentPeriod();
	}

	setVatPaymentPeriod(value) {
		this._vatPaymentModel.setVatPaymentPeriod(value);
	}

	getVatPaymentLatency() {
		return this._vatPaymentModel.getVatPaymentLatency();
	}

	setVatPaymentLatency(value) {
		this._vatPaymentModel.setVatPaymentLatency(value);
	}

	getForecastTemplateDescription() {
		return this._forecastTemplateDescription;
	}

	setForecastTemplateDescription(description) {
		this._forecastTemplateDescription = description;
	}

	serializeCashConversionResiduals() {
		return {
			accountPayables: {
				residualsSpread: this._existingAccountsPayableDelays,
				residualAccountSpecification:
					this._existingAccountsPayable.serialize(),
			},
			accountReceivables: {
				residualsSpread: this._existingAccountsReceivableDelays,
				residualAccountSpecification:
					this._existingAccountsReceivable.serialize(),
			},
		};
	}

	_serializedProperties() {
		return {
			numberOfReferencePeriods: this._numberOfReferencePeriods,
			numberOfPredictedPeriods: this._numberOfPredictedPeriods,
			chartOfAccountsId: this._chartOfAccountsId,
			cashAccount: this._bankAccount,
			accountsPayableConfiguration:
				this._forecastAccountsPayableCashConversionConfiguration,
			accountsReceivableConfiguration:
				this._forecastAccountsReceivableCashConversionConfiguration,
			taxAccount: this._taxAccount,
			incomingVatAccount: this._incomingVat,
			outgoingVatAccount: this._outgoingVat,
			sumVatAccount: this._vatSumAccount,
			incomingVatGroup: this._incomingVatGroup,
			outgoingVatGroup: this._outgoingVatGroup,
			sumVatGroup: this._sumVatGroup,
			vatPaymentConfiguration: this._vatPaymentModel,
			cashConversionResidualsConfiguration:
				this.serializeCashConversionResiduals(),
			forecastTemplateDescription: this._forecastTemplateDescription,
			frozenCurrentPeriod:
				this._frozenCurrentPeriod &&
				MonthPeriod.fromMonth(this._frozenCurrentPeriod),
		};
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.forecastAccountsPayableCashConversionConfiguration =
			CashConversionConfiguration.materialize(
				data.accountsPayableConfiguration,
				context,
			);

		data.forecastAccountsReceivableCashConversionConfiguration =
			CashConversionConfiguration.materialize(
				data.accountsReceivableConfiguration,
				context,
			);

		data.incomingVat = data.incomingVatAccount;
		data.outgoingVat = data.outgoingVatAccount;
		data.vatSumAccount = data.sumVatAccount;

		data.vatPaymentModel =
			data.vatPaymentConfiguration &&
			VatPaymentSettingModel.materialize(
				data.vatPaymentConfiguration,
				context,
			);

		const accountsPayableConfiguration =
			data.cashConversionResidualsConfiguration.accountPayables;

		data.accountsPayableDelays =
			accountsPayableConfiguration.residualsSpread;

		data.accountsPayable = specificationFactory.materialize(
			accountsPayableConfiguration.residualAccountSpecification,
			context,
		);

		const existingAccountsPayableConfiguration =
			data.cashConversionResidualsConfiguration.accountPayables;

		data.existingAccountsPayableDelays =
			existingAccountsPayableConfiguration.residualsSpread;

		data.existingAccountsPayable = specificationFactory.materialize(
			existingAccountsPayableConfiguration.residualAccountSpecification,
			context,
		);

		const existingAccountsReceivableConfiguration =
			data.cashConversionResidualsConfiguration.accountReceivables;

		data.existingAccountsReceivableDelays =
			existingAccountsReceivableConfiguration.residualsSpread;

		data.existingAccountsReceivable = specificationFactory.materialize(
			existingAccountsReceivableConfiguration.residualAccountSpecification,
			context,
		);

		data.incomingVatGroup &&= specificationFactory.materialize(
			data.incomingVatGroup,
		);

		data.outgoingVatGroup &&= specificationFactory.materialize(
			data.outgoingVatGroup,
		);

		data.sumVatGroup &&= specificationFactory.materialize(data.sumVatGroup);

		data.bankAccount = data.cashAccount;

		data.frozenCurrentPeriod &&= MonthPeriod.materialize(
			data.frozenCurrentPeriod,
			context,
		).asPeriod();
	}
}
